import { create } from 'zustand';
import type { Connection, EdgeChange, NodeChange, Viewport } from '@xyflow/react';
import { reconnectEdge } from '@xyflow/react';
import type { EditorEdge, EditorGraph, EditorNode } from '../domain/editor-graph';
import type { XYPosition } from '@xyflow/react';
import { createNodeFromDefinition } from '../nodes/domain/node-factory';
import { cloneGraph, graphContentEquals, HISTORY_LIMIT } from '../domain/editor-graph';
import {
  alignNodes,
  deleteSelection as deleteGraphSelection,
  distributeNodes,
  layoutGraph,
  pasteNodesAndEdges,
  setNodesLocked,
  type Alignment,
  type Distribution,
} from '../domain/transforms';
import { validateConnection } from '../edges/domain/validate-connection';
import { createEdgeFromCandidate } from '../edges/domain/edge-factory';
import { getEdgeDefinition } from '../edges/registry/edge-registry';
import type { EdgeTypeId } from '@/domain/funnel/edge-system';
import type { MetricInput } from '@/domain/metrics/contracts';
import {
  createCustomScenario as createCustomScenarioPure,
  deleteScenario as deleteScenarioPure,
  duplicateScenario as duplicateScenarioPure,
  ensureDefaultScenarios,
  renameScenario as renameScenarioPure,
  resetScenario as resetScenarioPure,
  setActiveScenario as setActiveScenarioPure,
  setEdgeOverrideConversion as setEdgeOverrideConversionPure,
  setEdgeReverseShare as setEdgeReverseSharePure,
  clearEdgeOverrideField as clearEdgeOverrideFieldPure,
  setNodeOverrideField as setNodeOverrideFieldPure,
  clearNodeOverrideField as clearNodeOverrideFieldPure,
  setScenarioTarget as setScenarioTargetPure,
  type NodeOverrideField,
} from '@/domain/simulation/scenarios/scenario-crud';
import type { SimulationTarget } from '@/domain/simulation/contracts';
import { readSimulationExtension } from '../simulation/graph-bridge';
import {
  createHighlight as createHighlightPure,
  createStrategicNote as createStrategicNotePure,
  deleteHighlight as deleteHighlightPure,
  deleteStrategicNote as deleteStrategicNotePure,
  reorderHighlights as reorderHighlightsPure,
  reorderStrategicNotes as reorderStrategicNotesPure,
  setPresentationBranding as setPresentationBrandingPure,
  setPresentationDefaultViewport as setPresentationDefaultViewportPure,
  setPresentationLogoAsset as setPresentationLogoAssetPure,
  setPresentationTheme as setPresentationThemePure,
  updateHighlight as updateHighlightPure,
  updatePresentationMeta as updatePresentationMetaPure,
  updateStrategicNote as updateStrategicNotePure,
} from '@/domain/presentation/presentation-crud';
import type {
  Highlight,
  PresentationBrandingMode,
  PresentationGraphExtension,
  PresentationTheme,
  PresentationViewport,
  StrategicNote,
} from '@/domain/presentation/contracts';
import { readPresentationExtension } from '../presentation/graph-bridge';
import type { DocumentationGraphExtension } from '@/domain/documentation/contracts';
import { DocumentationGraphExtensionSchema } from '@/domain/documentation/contracts';

interface EditorState {
  graph: EditorGraph | null;
  selectedIds: string[];
  selectedEdgeIds: string[];
  past: EditorGraph[];
  future: EditorGraph[];
  clipboard: EditorNode[];
  edgeClipboard: EditorEdge[];
  pasteIndex: number;
  dirty: boolean;
  dragOrigin: EditorGraph | null;
  layoutError: string | null;
  connectionError: string | null;
  persistedNodeIds: string[];
  /** SPEC-007 — bumped on every persistable graph/viewport mutation so the
   * autosave hook can notify the coordinator without re-deriving "was this
   * a real change" itself; selection-only changes never touch these. */
  mutationVersion: number;
  viewportVersion: number;
  markGraphPersisted: (nodeIds: string[]) => void;
  /** SPEC-007 SAVE-REQ-019 — applying a recovered local graph is itself a
   * mutation: it must be dirty and trigger autosave, never silently clean. */
  markRecovered: () => void;
  initialize: (graph: EditorGraph) => void;
  setSelection: (ids: string[]) => void;
  setEdgeSelection: (ids: string[]) => void;
  clearSelection: () => void;
  selectAll: () => void;
  applyNodeChanges: (changes: NodeChange<EditorNode>[]) => void;
  applyEdgeChanges: (changes: EdgeChange<EditorEdge>[]) => void;
  connectNodes: (connection: Connection, type?: EdgeTypeId) => void;
  reconnectEdgeAction: (oldEdge: EditorEdge, connection: Connection) => void;
  beginDrag: () => void;
  endDrag: () => void;
  setViewport: (viewport: Viewport) => void;
  addNode: (type: string, position?: XYPosition) => void;
  updateNodeData: (id: string, patch: Record<string, unknown>) => void;
  updateSelectedNodeData: (patch: Record<string, unknown>) => void;
  updateEdgeData: (id: string, patch: Record<string, unknown>) => void;
  updateSelectedEdgeData: (patch: Record<string, unknown>) => void;
  changeEdgeType: (id: string, type: EdgeTypeId) => void;
  /** SPEC-008 — merges a patch into `node.data.metrics[key]` (e.g. `{ mode: 'manual', manualValue: 500 }`). */
  updateNodeMetric: (nodeId: string, key: string, patch: Record<string, unknown>) => void;
  updateNodeMetricRoot: (nodeId: string, patch: Record<string, unknown>) => void;
  /** Merges a patch into `edge.data.metrics[key]` (only `conversion` exists today). */
  updateEdgeMetric: (edgeId: string, key: string, patch: Record<string, unknown>) => void;
  copy: () => void;
  paste: () => void;
  deleteSelection: () => void;
  setLocked: (locked: boolean) => void;
  align: (alignment: Alignment) => void;
  distribute: (direction: Distribution) => void;
  autoLayout: () => void;
  undo: () => void;
  redo: () => void;
  /** SPEC-009 — ensures the three canonical scenarios exist; a no-op commit when they already do. */
  ensureSimulationDefaults: () => void;
  setActiveScenario: (scenarioId: string | null) => void;
  createCustomScenario: (name: string) => void;
  duplicateScenario: (scenarioId: string, name?: string) => void;
  renameScenario: (scenarioId: string, name: string) => void;
  deleteScenario: (scenarioId: string) => void;
  resetScenario: (scenarioId: string) => void;
  setScenarioTarget: (scenarioId: string, target: SimulationTarget | undefined) => void;
  setNodeOverrideField: (
    scenarioId: string,
    nodeId: string,
    field: NodeOverrideField,
    value: MetricInput,
  ) => void;
  clearNodeOverrideField: (scenarioId: string, nodeId: string, field: NodeOverrideField) => void;
  setEdgeOverrideConversion: (scenarioId: string, edgeId: string, value: MetricInput) => void;
  setEdgeReverseShare: (scenarioId: string, edgeId: string, share: number) => void;
  clearEdgeOverrideField: (
    scenarioId: string,
    edgeId: string,
    field: 'conversion' | 'reverseShare',
  ) => void;
  // SPEC-011 — presentation config CRUD (graph.presentation), same commit()/undo/autosave pipeline.
  updatePresentationMeta: (
    patch: Partial<
      Pick<
        PresentationGraphExtension,
        'title' | 'subtitle' | 'companyName' | 'showMetrics' | 'coverEnabled'
      >
    >,
  ) => void;
  setPresentationTheme: (theme: PresentationTheme) => void;
  setPresentationBranding: (brandingMode: PresentationBrandingMode) => void;
  setPresentationLogoAsset: (assetId: string | undefined) => void;
  setPresentationDefaultViewport: (viewport: PresentationViewport) => void;
  /** SPEC-017 — replaces the validated extension; generated documentation is never persisted. */
  setDocumentation: (extension: DocumentationGraphExtension) => void;
  createStrategicNote: (input: { title: string; body?: string; nodeIds?: string[] }) => void;
  updateStrategicNote: (
    noteId: string,
    patch: Partial<Pick<StrategicNote, 'title' | 'body' | 'nodeIds' | 'visible'>>,
  ) => void;
  deleteStrategicNote: (noteId: string) => void;
  reorderStrategicNotes: (orderedIds: string[]) => void;
  createHighlight: (input: {
    label: string;
    nodeIds?: string[];
    edgeIds?: string[];
    emphasis?: Highlight['emphasis'];
    noteId?: string;
  }) => void;
  updateHighlight: (
    highlightId: string,
    patch: Partial<Pick<Highlight, 'label' | 'nodeIds' | 'edgeIds' | 'emphasis' | 'noteId'>>,
  ) => void;
  deleteHighlight: (highlightId: string) => void;
  reorderHighlights: (orderedIds: string[]) => void;
}

interface Selection {
  nodeIds: readonly string[];
  edgeIds: readonly string[];
}

function applySelection(graph: EditorGraph, selection: Selection): EditorGraph {
  const nodeIds = new Set(selection.nodeIds);
  const edgeIds = new Set(selection.edgeIds);
  return {
    ...graph,
    nodes: graph.nodes.map((node) => ({
      ...node,
      selected: nodeIds.has(node.id),
      draggable: !node.locked,
    })),
    edges: graph.edges.map((edge) => ({ ...edge, selected: edgeIds.has(edge.id) })),
  };
}

export const useEditorStore = create<EditorState>((set, get) => {
  const commit = (transform: (graph: EditorGraph) => EditorGraph, selection?: Selection) => {
    const state = get();
    if (!state.graph) return;
    const next = transform(cloneGraph(state.graph));
    if (graphContentEquals(state.graph, next)) return;
    const nextNodeIds = new Set(next.nodes.map((node) => node.id));
    const nextEdgeIds = new Set(next.edges.map((edge) => edge.id));
    const resolved: Selection = selection ?? {
      nodeIds: state.selectedIds.filter((id) => nextNodeIds.has(id)),
      edgeIds: state.selectedEdgeIds.filter((id) => nextEdgeIds.has(id)),
    };
    set({
      graph: applySelection(next, resolved),
      selectedIds: [...resolved.nodeIds],
      selectedEdgeIds: [...resolved.edgeIds],
      past: [...state.past, cloneGraph(state.graph)].slice(-HISTORY_LIMIT),
      future: [],
      dirty: true,
      layoutError: null,
      mutationVersion: state.mutationVersion + 1,
    });
  };

  return {
    graph: null,
    selectedIds: [],
    selectedEdgeIds: [],
    past: [],
    future: [],
    clipboard: [],
    edgeClipboard: [],
    pasteIndex: 0,
    dirty: false,
    dragOrigin: null,
    layoutError: null,
    connectionError: null,
    persistedNodeIds: [],
    mutationVersion: 0,
    viewportVersion: 0,
    markGraphPersisted: (nodeIds) => set({ persistedNodeIds: nodeIds }),
    markRecovered: () =>
      set((state) =>
        state.graph ? { dirty: true, mutationVersion: state.mutationVersion + 1 } : state,
      ),
    initialize: (graph) =>
      set({
        graph: cloneGraph(graph),
        selectedIds: [],
        selectedEdgeIds: [],
        past: [],
        future: [],
        clipboard: [],
        edgeClipboard: [],
        pasteIndex: 0,
        dirty: false,
        dragOrigin: null,
        layoutError: null,
        connectionError: null,
        persistedNodeIds: graph.nodes.map((node) => node.id),
        mutationVersion: 0,
        viewportVersion: 0,
      }),
    setSelection: (selectedIds) =>
      set((state) => {
        const unchanged =
          state.selectedEdgeIds.length === 0 &&
          selectedIds.length === state.selectedIds.length &&
          selectedIds.every((id, index) => id === state.selectedIds[index]);
        if (unchanged) return state;
        return state.graph
          ? {
              selectedIds,
              selectedEdgeIds: [],
              graph: applySelection(state.graph, { nodeIds: selectedIds, edgeIds: [] }),
            }
          : { selectedIds, selectedEdgeIds: [] };
      }),
    setEdgeSelection: (selectedEdgeIds) =>
      set((state) => {
        const unchanged =
          state.selectedIds.length === 0 &&
          selectedEdgeIds.length === state.selectedEdgeIds.length &&
          selectedEdgeIds.every((id, index) => id === state.selectedEdgeIds[index]);
        if (unchanged) return state;
        return state.graph
          ? {
              selectedEdgeIds,
              selectedIds: [],
              graph: applySelection(state.graph, { nodeIds: [], edgeIds: selectedEdgeIds }),
            }
          : { selectedEdgeIds, selectedIds: [] };
      }),
    clearSelection: () => get().setSelection([]),
    selectAll: () => {
      const graph = get().graph;
      if (graph) get().setSelection(graph.nodes.map((node) => node.id));
    },
    applyNodeChanges: (changes) =>
      set((state) => {
        if (!state.graph) return state;
        const positions = new Map<string, { x: number; y: number }>();
        const selected = new Set(state.selectedIds);
        let selectionChanged = false;
        for (const change of changes) {
          if (change.type === 'position' && change.position)
            positions.set(change.id, change.position);
          if (change.type === 'select') {
            selectionChanged = true;
            if (change.selected) selected.add(change.id);
            else selected.delete(change.id);
          }
        }
        if (positions.size === 0 && !selectionChanged) return state;
        const selectedIds = [...selected];
        return {
          selectedIds,
          selectedEdgeIds: selectionChanged ? [] : state.selectedEdgeIds,
          graph: applySelection(
            {
              ...state.graph,
              nodes: state.graph.nodes.map((node) =>
                !node.locked && positions.has(node.id)
                  ? { ...node, position: positions.get(node.id)! }
                  : node,
              ),
            },
            {
              nodeIds: selectedIds,
              edgeIds: selectionChanged ? [] : state.selectedEdgeIds,
            },
          ),
        };
      }),
    applyEdgeChanges: (changes) =>
      set((state) => {
        if (!state.graph) return state;
        const selected = new Set(state.selectedEdgeIds);
        let selectionChanged = false;
        for (const change of changes) {
          if (change.type === 'select') {
            selectionChanged = true;
            if (change.selected) selected.add(change.id);
            else selected.delete(change.id);
          }
        }
        if (!selectionChanged) return state;
        const selectedEdgeIds = [...selected];
        return {
          selectedEdgeIds,
          selectedIds: [],
          graph: applySelection(state.graph, { nodeIds: [], edgeIds: selectedEdgeIds }),
        };
      }),
    connectNodes: (connection, type = 'standard') => {
      const graph = get().graph;
      if (!graph) return;
      const candidate = {
        source: connection.source,
        target: connection.target,
        sourceHandle: connection.sourceHandle,
        targetHandle: connection.targetHandle,
      };
      const result = validateConnection(graph, candidate, type);
      if (!result.valid) {
        set({ connectionError: result.reason ?? 'This connection is not allowed.' });
        return;
      }
      set({ connectionError: null });
      const edge = createEdgeFromCandidate(candidate, type);
      commit((current) => ({ ...current, edges: [...current.edges, edge] }), {
        nodeIds: [],
        edgeIds: [edge.id],
      });
    },
    reconnectEdgeAction: (oldEdge, connection) => {
      const graph = get().graph;
      if (!graph) return;
      const candidate = {
        source: connection.source,
        target: connection.target,
        sourceHandle: connection.sourceHandle,
        targetHandle: connection.targetHandle,
      };
      const others = graph.edges.filter((edge) => edge.id !== oldEdge.id);
      const result = validateConnection(
        { ...graph, edges: others },
        candidate,
        (oldEdge.type as EdgeTypeId) ?? 'standard',
      );
      if (!result.valid) {
        set({ connectionError: result.reason ?? 'This connection is not allowed.' });
        return;
      }
      set({ connectionError: null });
      commit(
        (current) => ({
          ...current,
          edges: reconnectEdge(oldEdge, connection, current.edges as EditorEdge[]),
        }),
        { nodeIds: [], edgeIds: [oldEdge.id] },
      );
    },
    beginDrag: () => {
      const graph = get().graph;
      if (graph) set({ dragOrigin: cloneGraph(graph) });
    },
    endDrag: () => {
      const state = get();
      if (!state.graph || !state.dragOrigin) return;
      if (graphContentEquals(state.graph, state.dragOrigin)) return set({ dragOrigin: null });
      set({
        past: [...state.past, state.dragOrigin].slice(-HISTORY_LIMIT),
        future: [],
        dirty: true,
        dragOrigin: null,
        mutationVersion: state.mutationVersion + 1,
      });
    },
    setViewport: (viewport) =>
      set((state) =>
        state.graph
          ? { graph: { ...state.graph, viewport }, viewportVersion: state.viewportVersion + 1 }
          : state,
      ),
    addNode: (type, position) => {
      const graph = get().graph;
      if (!graph) return;
      const index = graph.nodes.length;
      const target = position ?? {
        x: (300 - graph.viewport.x) / graph.viewport.zoom + (index % 3) * 220,
        y: (180 - graph.viewport.y) / graph.viewport.zoom + Math.floor(index / 3) * 150,
      };
      const node = createNodeFromDefinition(type, target);
      commit((current) => ({ ...current, nodes: [...current.nodes, node] }), {
        nodeIds: [node.id],
        edgeIds: [],
      });
    },
    updateNodeData: (id, patch) =>
      commit((graph) => ({
        ...graph,
        nodes: graph.nodes.map((node) =>
          node.id === id ? { ...node, data: { ...node.data, ...patch } } : node,
        ),
      })),
    updateSelectedNodeData: (patch) => {
      const ids = new Set(get().selectedIds);
      commit((graph) => ({
        ...graph,
        nodes: graph.nodes.map((node) =>
          ids.has(node.id) ? { ...node, data: { ...node.data, ...patch } } : node,
        ),
      }));
    },
    updateEdgeData: (id, patch) =>
      commit((graph) => ({
        ...graph,
        edges: graph.edges.map((edge) =>
          edge.id === id ? { ...edge, data: { ...edge.data, ...patch } } : edge,
        ),
      })),
    updateSelectedEdgeData: (patch) => {
      const ids = new Set(get().selectedEdgeIds);
      commit((graph) => ({
        ...graph,
        edges: graph.edges.map((edge) =>
          ids.has(edge.id) ? { ...edge, data: { ...edge.data, ...patch } } : edge,
        ),
      }));
    },
    changeEdgeType: (id, type) => {
      const definition = getEdgeDefinition(type);
      commit((graph) => ({
        ...graph,
        edges: graph.edges.map((edge) =>
          edge.id === id
            ? {
                ...edge,
                type,
                data: {
                  ...edge.data,
                  style: definition?.defaultData.style ?? edge.data?.style,
                  animated: definition?.defaultData.animated ?? edge.data?.animated,
                },
              }
            : edge,
        ),
      }));
    },
    updateNodeMetric: (nodeId, key, patch) =>
      commit((graph) => ({
        ...graph,
        nodes: graph.nodes.map((node) => {
          if (node.id !== nodeId) return node;
          const currentMetrics = (node.data.metrics as Record<string, unknown> | undefined) ?? {};
          const currentField = (currentMetrics[key] as Record<string, unknown> | undefined) ?? {};
          return {
            ...node,
            data: {
              ...node.data,
              metrics: { ...currentMetrics, [key]: { ...currentField, ...patch } },
            },
          };
        }),
      })),
    updateNodeMetricRoot: (nodeId, patch) =>
      commit((graph) => ({
        ...graph,
        nodes: graph.nodes.map((node) => {
          if (node.id !== nodeId) return node;
          const currentMetrics = (node.data.metrics as Record<string, unknown> | undefined) ?? {};
          return { ...node, data: { ...node.data, metrics: { ...currentMetrics, ...patch } } };
        }),
      })),
    updateEdgeMetric: (edgeId, key, patch) =>
      commit((graph) => ({
        ...graph,
        edges: graph.edges.map((edge) => {
          if (edge.id !== edgeId) return edge;
          const currentMetrics = (edge.data?.metrics as Record<string, unknown> | undefined) ?? {};
          const currentField = (currentMetrics[key] as Record<string, unknown> | undefined) ?? {};
          return {
            ...edge,
            data: {
              ...edge.data,
              metrics: { ...currentMetrics, [key]: { ...currentField, ...patch } },
            },
          };
        }),
      })),
    ensureSimulationDefaults: () =>
      commit((graph) => ({
        ...graph,
        simulation: ensureDefaultScenarios(readSimulationExtension(graph)),
      })),
    setActiveScenario: (scenarioId) =>
      commit((graph) => ({
        ...graph,
        simulation: setActiveScenarioPure(readSimulationExtension(graph), scenarioId),
      })),
    createCustomScenario: (name) =>
      commit((graph) => ({
        ...graph,
        simulation: createCustomScenarioPure(readSimulationExtension(graph), name).extension,
      })),
    duplicateScenario: (scenarioId, name) =>
      commit((graph) => {
        const result = duplicateScenarioPure(readSimulationExtension(graph), scenarioId, name);
        return result ? { ...graph, simulation: result.extension } : graph;
      }),
    renameScenario: (scenarioId, name) =>
      commit((graph) => ({
        ...graph,
        simulation: renameScenarioPure(readSimulationExtension(graph), scenarioId, name),
      })),
    deleteScenario: (scenarioId) =>
      commit((graph) => ({
        ...graph,
        simulation: deleteScenarioPure(readSimulationExtension(graph), scenarioId),
      })),
    resetScenario: (scenarioId) =>
      commit((graph) => ({
        ...graph,
        simulation: resetScenarioPure(readSimulationExtension(graph), scenarioId),
      })),
    setScenarioTarget: (scenarioId, target) =>
      commit((graph) => ({
        ...graph,
        simulation: setScenarioTargetPure(readSimulationExtension(graph), scenarioId, target),
      })),
    setNodeOverrideField: (scenarioId, nodeId, field, value) =>
      commit((graph) => ({
        ...graph,
        simulation: setNodeOverrideFieldPure(
          readSimulationExtension(graph),
          scenarioId,
          nodeId,
          field,
          value,
        ),
      })),
    clearNodeOverrideField: (scenarioId, nodeId, field) =>
      commit((graph) => ({
        ...graph,
        simulation: clearNodeOverrideFieldPure(
          readSimulationExtension(graph),
          scenarioId,
          nodeId,
          field,
        ),
      })),
    setEdgeOverrideConversion: (scenarioId, edgeId, value) =>
      commit((graph) => ({
        ...graph,
        simulation: setEdgeOverrideConversionPure(
          readSimulationExtension(graph),
          scenarioId,
          edgeId,
          value,
        ),
      })),
    setEdgeReverseShare: (scenarioId, edgeId, share) =>
      commit((graph) => ({
        ...graph,
        simulation: setEdgeReverseSharePure(
          readSimulationExtension(graph),
          scenarioId,
          edgeId,
          share,
        ),
      })),
    clearEdgeOverrideField: (scenarioId, edgeId, field) =>
      commit((graph) => ({
        ...graph,
        simulation: clearEdgeOverrideFieldPure(
          readSimulationExtension(graph),
          scenarioId,
          edgeId,
          field,
        ),
      })),
    updatePresentationMeta: (patch) =>
      commit((graph) => ({
        ...graph,
        presentation: updatePresentationMetaPure(readPresentationExtension(graph), patch),
      })),
    setPresentationTheme: (theme) =>
      commit((graph) => ({
        ...graph,
        presentation: setPresentationThemePure(readPresentationExtension(graph), theme),
      })),
    setPresentationBranding: (brandingMode) =>
      commit((graph) => ({
        ...graph,
        presentation: setPresentationBrandingPure(readPresentationExtension(graph), brandingMode),
      })),
    setPresentationLogoAsset: (assetId) =>
      commit((graph) => ({
        ...graph,
        presentation: setPresentationLogoAssetPure(readPresentationExtension(graph), assetId),
      })),
    setPresentationDefaultViewport: (viewport) =>
      commit((graph) => ({
        ...graph,
        presentation: setPresentationDefaultViewportPure(
          readPresentationExtension(graph),
          viewport,
        ),
      })),
    setDocumentation: (extension) => {
      const parsed = DocumentationGraphExtensionSchema.safeParse(extension);
      if (!parsed.success) return;
      commit((graph) => ({ ...graph, documentation: parsed.data }));
    },
    createStrategicNote: (input) =>
      commit((graph) => ({
        ...graph,
        presentation: createStrategicNotePure(readPresentationExtension(graph), input).extension,
      })),
    updateStrategicNote: (noteId, patch) =>
      commit((graph) => ({
        ...graph,
        presentation: updateStrategicNotePure(readPresentationExtension(graph), noteId, patch),
      })),
    deleteStrategicNote: (noteId) =>
      commit((graph) => ({
        ...graph,
        presentation: deleteStrategicNotePure(readPresentationExtension(graph), noteId),
      })),
    reorderStrategicNotes: (orderedIds) =>
      commit((graph) => ({
        ...graph,
        presentation: reorderStrategicNotesPure(readPresentationExtension(graph), orderedIds),
      })),
    createHighlight: (input) =>
      commit((graph) => ({
        ...graph,
        presentation: createHighlightPure(readPresentationExtension(graph), input).extension,
      })),
    updateHighlight: (highlightId, patch) =>
      commit((graph) => ({
        ...graph,
        presentation: updateHighlightPure(readPresentationExtension(graph), highlightId, patch),
      })),
    deleteHighlight: (highlightId) =>
      commit((graph) => ({
        ...graph,
        presentation: deleteHighlightPure(readPresentationExtension(graph), highlightId),
      })),
    reorderHighlights: (orderedIds) =>
      commit((graph) => ({
        ...graph,
        presentation: reorderHighlightsPure(readPresentationExtension(graph), orderedIds),
      })),
    copy: () => {
      const state = get();
      if (!state.graph) return;
      const nodeIds = new Set(state.selectedIds);
      const edgeIds = new Set(state.selectedEdgeIds);
      set({
        clipboard: structuredClone(state.graph.nodes.filter((node) => nodeIds.has(node.id))),
        edgeClipboard: structuredClone(
          state.graph.edges.filter(
            (edge) =>
              edgeIds.has(edge.id) || (nodeIds.has(edge.source) && nodeIds.has(edge.target)),
          ),
        ),
        pasteIndex: 0,
      });
    },
    paste: () => {
      const state = get();
      if (!state.graph || state.clipboard.length === 0) return;
      const result = pasteNodesAndEdges(
        state.graph,
        state.clipboard,
        state.edgeClipboard,
        state.pasteIndex + 1,
      );
      commit(() => result.graph, {
        nodeIds: result.selectedNodeIds,
        edgeIds: result.selectedEdgeIds,
      });
      set({ pasteIndex: state.pasteIndex + 1 });
    },
    deleteSelection: () => {
      const nodeIds = new Set(get().selectedIds);
      const edgeIds = new Set(get().selectedEdgeIds);
      commit((graph) => deleteGraphSelection(graph, nodeIds, edgeIds), {
        nodeIds: [],
        edgeIds: [],
      });
    },
    setLocked: (locked) => {
      const ids = new Set(get().selectedIds);
      commit((graph) => setNodesLocked(graph, ids, locked));
    },
    align: (alignment) => {
      const ids = new Set(get().selectedIds);
      commit((graph) => alignNodes(graph, ids, alignment));
    },
    distribute: (direction) => {
      const ids = new Set(get().selectedIds);
      commit((graph) => distributeNodes(graph, ids, direction));
    },
    autoLayout: () => {
      try {
        commit((graph) => layoutGraph(graph));
      } catch {
        set({
          layoutError: 'Auto-layout could not be applied. Existing positions were preserved.',
        });
      }
    },
    undo: () => {
      const state = get();
      const previous = state.past.at(-1);
      if (!state.graph || !previous) return;
      const selection: Selection = {
        nodeIds: state.selectedIds.filter((id) => previous.nodes.some((node) => node.id === id)),
        edgeIds: state.selectedEdgeIds.filter((id) =>
          previous.edges.some((edge) => edge.id === id),
        ),
      };
      set({
        graph: applySelection(cloneGraph(previous), selection),
        selectedIds: [...selection.nodeIds],
        selectedEdgeIds: [...selection.edgeIds],
        past: state.past.slice(0, -1),
        future: [cloneGraph(state.graph), ...state.future].slice(0, HISTORY_LIMIT),
        dirty: true,
        mutationVersion: state.mutationVersion + 1,
      });
    },
    redo: () => {
      const state = get();
      const next = state.future[0];
      if (!state.graph || !next) return;
      const selection: Selection = {
        nodeIds: state.selectedIds.filter((id) => next.nodes.some((node) => node.id === id)),
        edgeIds: state.selectedEdgeIds.filter((id) => next.edges.some((edge) => edge.id === id)),
      };
      set({
        graph: applySelection(cloneGraph(next), selection),
        selectedIds: [...selection.nodeIds],
        selectedEdgeIds: [...selection.edgeIds],
        past: [...state.past, cloneGraph(state.graph)].slice(-HISTORY_LIMIT),
        future: state.future.slice(1),
        dirty: true,
        mutationVersion: state.mutationVersion + 1,
      });
    },
  };
});
